/**
 * Tone generation.
 *
 * One oscillator per sounding note. The richer voices are built as
 * `PeriodicWave`s rather than stacks of oscillators: a single oscillator with a
 * defined harmonic spectrum is cheaper, and more importantly its partials stay
 * in exact whole-number ratios, so nothing inside the reference tone can beat
 * against itself and confuse the measurement.
 *
 * Deliberately no compressor or limiter anywhere in the chain. Any dynamics
 * processing would modulate the amplitude of the reference tone, and amplitude
 * modulation is precisely the quantity beat mode measures.
 */

import { audioContext, resumeAudio } from "./audio.js";

export interface Waveform {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Harmonic amplitudes, index 1 = fundamental. Index 0 must be 0 (DC). */
  readonly partials: readonly number[];
}

/**
 * A pure sine is the worst possible tone to tune against by ear: with nothing
 * but a fundamental there is only one beat to hear, and it dies away to nothing
 * exactly where you need the most resolution. Real stops give you the upper
 * partials too, and those beat faster and far more audibly.
 */
export const WAVEFORMS: readonly Waveform[] = [
  {
    id: "sine",
    name: "Sine",
    description: "Fundamental only. Cleanest reference, hardest to beat against.",
    partials: [0, 1],
  },
  {
    id: "flute",
    name: "Flute",
    description: "Nearly pure with a touch of octave. Like a stopped diapason.",
    partials: [0, 1, 0.18, 0.05, 0.02],
  },
  {
    id: "principal",
    name: "Principal",
    description: "Even harmonic taper. The general-purpose choice for tuning flue work.",
    partials: [0, 1, 0.5, 0.33, 0.22, 0.15, 0.1, 0.07, 0.05, 0.035],
  },
  {
    id: "string",
    name: "String",
    description: "Bright and harmonically dense, like a gamba. Beats very audibly.",
    partials: [0, 1, 0.7, 0.55, 0.5, 0.45, 0.4, 0.34, 0.28, 0.22, 0.18, 0.14],
  },
  {
    id: "reed",
    name: "Reed",
    description: "Buzzy and full. Useful when tuning against reed stops.",
    partials: [0, 1, 0.8, 0.66, 0.52, 0.44, 0.36, 0.3, 0.25, 0.21, 0.17, 0.14, 0.11, 0.09],
  },
];

export function waveformById(id: string): Waveform {
  return WAVEFORMS.find((w) => w.id === id) ?? WAVEFORMS[0]!;
}

const ATTACK_SECONDS = 0.02;
const RELEASE_SECONDS = 0.08;
/** Per-voice amplitude, low enough that a handful of notes cannot clip. */
const VOICE_GAIN = 0.15;
/** Retune ramp, long enough not to click and short enough to feel instant. */
const GLIDE_SECONDS = 0.02;

interface Voice {
  readonly oscillator: OscillatorNode;
  readonly gain: GainNode;
  frequency: number;
}

export class ToneEngine {
  private master: GainNode | null = null;
  private readonly voices = new Map<string, Voice>();
  private waveform: Waveform = waveformById("principal");
  private wave: PeriodicWave | null = null;
  private volume = 0.6;

  private ensureMaster(): GainNode {
    if (!this.master) {
      const ctx = audioContext();
      this.master = ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(ctx.destination);
    }
    return this.master;
  }

  private periodicWave(): PeriodicWave | null {
    if (this.waveform.id === "sine") return null;
    if (!this.wave) {
      const partials = this.waveform.partials;
      const real = new Float32Array(partials.length);
      const imag = Float32Array.from(partials);
      imag[0] = 0;
      this.wave = audioContext().createPeriodicWave(real, imag, {
        // Let the browser scale the result to unit peak, so switching voices
        // does not change the perceived loudness.
        disableNormalization: false,
      });
    }
    return this.wave;
  }

  /** Must be called from a user gesture before the first note on mobile. */
  async unlock(): Promise<void> {
    await resumeAudio();
    this.ensureMaster();
  }

  get waveformId(): string {
    return this.waveform.id;
  }

  setWaveform(id: string): void {
    const next = waveformById(id);
    if (next.id === this.waveform.id) return;
    this.waveform = next;
    this.wave = null;

    const wave = this.periodicWave();
    for (const voice of this.voices.values()) {
      if (wave) voice.oscillator.setPeriodicWave(wave);
      else voice.oscillator.type = "sine";
    }
  }

  get outputVolume(): number {
    return this.volume;
  }

  setVolume(value: number): void {
    this.volume = Math.min(1, Math.max(0, value));
    const master = this.ensureMaster();
    const now = audioContext().currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setTargetAtTime(this.volume, now, 0.01);
  }

  isSounding(id: string): boolean {
    return this.voices.has(id);
  }

  soundingIds(): string[] {
    return [...this.voices.keys()];
  }

  /** Frequency a sounding voice is currently producing, or null. */
  frequencyOf(id: string): number | null {
    return this.voices.get(id)?.frequency ?? null;
  }

  noteOn(id: string, frequency: number): void {
    if (!(frequency > 0)) return;

    const existing = this.voices.get(id);
    if (existing) {
      this.setFrequency(id, frequency);
      return;
    }

    const ctx = audioContext();
    const master = this.ensureMaster();
    const now = ctx.currentTime;

    const oscillator = ctx.createOscillator();
    const wave = this.periodicWave();
    if (wave) oscillator.setPeriodicWave(wave);
    else oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(VOICE_GAIN, now + ATTACK_SECONDS);

    oscillator.connect(gain);
    gain.connect(master);
    oscillator.start(now);

    this.voices.set(id, { oscillator, gain, frequency });
  }

  /** Retune a sounding voice, e.g. after the reference pitch is changed. */
  setFrequency(id: string, frequency: number): void {
    const voice = this.voices.get(id);
    if (!voice || !(frequency > 0)) return;
    const now = audioContext().currentTime;
    voice.oscillator.frequency.cancelScheduledValues(now);
    voice.oscillator.frequency.setValueAtTime(voice.frequency, now);
    voice.oscillator.frequency.linearRampToValueAtTime(frequency, now + GLIDE_SECONDS);
    voice.frequency = frequency;
  }

  noteOff(id: string): void {
    const voice = this.voices.get(id);
    if (!voice) return;
    this.voices.delete(id);

    const now = audioContext().currentTime;
    const gain = voice.gain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.linearRampToValueAtTime(0, now + RELEASE_SECONDS);
    voice.oscillator.stop(now + RELEASE_SECONDS + 0.02);
    voice.oscillator.onended = () => {
      voice.oscillator.disconnect();
      voice.gain.disconnect();
    };
  }

  allOff(): void {
    for (const id of [...this.voices.keys()]) this.noteOff(id);
  }
}
